<template>
    <div>
        <slot :letters="letters" :alphabets="alphabets" :activeLetters="activeLetters" :orchestraList="orchestraList" :swapView="swapView" :view="view"></slot>
    </div>
</template>

<script>
import { defineComponent, onMounted, ref, watch } from 'vue';
import { getPeople } from '../services/inventory';
import { groupBy, sortBy, has, get, isArray, map, orderBy } from 'lodash';

export default defineComponent({
    name: 'OrchestraRoster',
    props: {
        sortOrder: {
            type: Array,
            required: false,
            default: [
                { role: 'conductors' },
                { role: 'strings' },
                { role: 'woodwinds' },
                { role: 'brass' },
                { role: 'percussion' },
                { role: 'keyboards' },
                { role: 'librarians' },
            ],
        },
        instrumentOrder: {
            type: Array,
            required: false,
            default: [
                // Strings
                'firstviolin', 'secondviolin', 'viola', 'cello', 'bass', 'harp',
                // Woodwinds
                'flute', 'oboe', 'clarinet', 'bassoon',
                // Brass
                'horn', 'trumpet', 'trombone', 'tuba',
                // Percussion
                'timpani', 'percussion',
                // Keyboards
                'keyboardinstrument',
                // Librarians
                'librarian',
            ],
        },
    },
    setup(props) {
        const letters = ref([]);
        const activeLetters = ref([]);
        const orchestraList = ref([]);
        const view = ref('instrument');

        onMounted(async () => {
            let people = await getPeople();

            people = people.filter(person => {
                return get(person, 'attributes.custom.roster_display') === true;
            });

            getNameViewData(people);
            getInstrumentViewData(people);
        })

        const alphabets = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ'.split('');

        const getNameViewData = (people) => {
            let groupedByAlphabet = groupBy(people, person => {
                if (person.lastName) {
                    return person.lastName[0].toUpperCase();
                }

                return person.firstName[0].toUpperCase();
            });

            activeLetters.value = Object.keys(groupedByAlphabet);

            groupedByAlphabet = map(groupedByAlphabet, (groupedPeople, letter) => {
                return {
                    title: letter.toUpperCase(),
                    participantGroup: orderBy(groupedPeople, [
                        person => {
                            return (person.lastName || '').toLowerCase();
                        },
                        person => {
                            return (person.firstName || '').toLowerCase();
                        },
                    ], ['asc', 'asc']),
                }
            });

            letters.value = sortBy(groupedByAlphabet, 'title');
        }

        const instrumentIndex = instrument => {
            if (!instrument) {
                return Number.MAX_SAFE_INTEGER;
            }

            const key = instrument.trim().replace(/ /g,'').toLowerCase();

            if (props.instrumentOrder.indexOf(key) !== -1) {
                return props.instrumentOrder.indexOf(key);
            }

            return Number.MAX_SAFE_INTEGER;
        }

        const sortOrder = props.sortOrder.map(item => {
            return item.role.trim().replace(/ /g,'').toLowerCase();
        })

        const orchestraIndex = orchestra => {
            if (!orchestra) {
                return Number.MAX_SAFE_INTEGER;
            }

            const key = orchestra.trim().replace(/ /g,'').toLowerCase();

            if (sortOrder.indexOf(key) !== -1) {
                return sortOrder.indexOf(key);
            }

            return Number.MAX_SAFE_INTEGER;
        }

        const getInstrumentViewData = (people) => {
            let peopleWithOrchestraGroup = people.filter(person => {
                return has(person, 'attributes.custom.orchestra_group'); // Must have orchestra_group && instrument
            })

            let groupedByOrchestra = groupBy(peopleWithOrchestraGroup, person => {
                return person.attributes.custom.orchestra_group;
            });

            groupedByOrchestra = map(groupedByOrchestra, (orchestraPeople, orchestra) => {
                let groupedByInstrument = groupBy(orchestraPeople, person => {
                    const instrument = get(person, 'attributes.custom.instrument');

                    if (isArray(instrument)) {
                        return instrument[0];
                    }

                    return instrument;
                });

                groupedByInstrument = map(groupedByInstrument, (instrumentPeople, instrument) => {
                    return {
                        title: instrument,
                        order: instrumentIndex(instrument),
                        people: orderBy(instrumentPeople, [
                            person => {
                                const sort = get(person, 'attributes.custom.sort');
                                return typeof sort === 'number' && sort > 0 ? 0 : 1;
                            },
                            person => {
                                const sort = get(person, 'attributes.custom.sort');
                                return typeof sort === 'number' && sort > 0 ? sort : Number.MAX_SAFE_INTEGER;
                            },
                            person => {
                                return (person.lastName || '').toLowerCase();
                            },
                            person => {
                                return (person.firstName || '').toLowerCase();
                            },
                        ], ['asc', 'asc', 'asc', 'asc']),
                    }
                });

                return {
                    title: orchestra,
                    order: orchestraIndex(orchestra),
                    instruments: sortBy(groupedByInstrument, 'order'),
                }
            });

            orchestraList.value = sortBy(groupedByOrchestra, 'order');
        }

        const swapView = (viewIn) => {
            view.value = viewIn;
        }

        return {
            letters,
            alphabets,
            activeLetters,
            orchestraList,
            swapView,
            view
        }
    },
});
</script>
